import axios from "../lib/axios";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";

/**
 * HTTP POST wrappers for the agency-linking onRequest functions (us-central1).
 * Auth is a Bearer Firebase ID token. Every body is the envelope
 * { ok, message?, code?, data? }. Field mapping lives in
 * utils/agencyAccountLinking.js.
 *
 * linkAdAccountsToAgency
 * { source: "connectionPicker"|"dashboardCard"|"agencySheet",
 *   copyVersion: "2148-v1", locale: "es"|"en"|"pt",
 *   accounts: [{ advertiserID?, platform, integrationID, adAccountID }],
 *   resumeBatchID? }
 * A new submit sends copyVersion "2148-v1". A resume sends resumeBatchID
 * and omits copyVersion. source stays required.
 * data: { batchID, summary, results, needsReauth }
 * results and links keys are `${platform}:${adAccountID}`.
 * 409 agencyLinkInProgress (no consent id), 403 agencyLinkConsentRequired,
 * 403 agencyLinkConsentRequiresClient,
 * 400 agencyLinkingNotConfigured (any account outside availablePlatforms).
 *
 * getAgencyLinkingPrompt {}
 * data: { show, variant, showPickerLine, targets,
 *   needsReauth, availablePlatforms }
 * show is honored as returned. "Not now" only hides the card for the
 * current page session.
 * Each target includes link status (unlinked, pending, linked, failed).
 * The backend reads agencyAccountLinks/{platform}/{adAccountID}; the web app
 * never reads those nodes. UI state comes from this endpoint (and from
 * linkAdAccountsToAgency results, then a prompt refresh). Normalized as
 * accounts / targetAccounts in utils/agencyAccountLinking.js.
 * Direct advertisers: show false, variant "direct", showPickerLine false.
 * No ad accounts yet: variant null (unclassified), show false, no auto direct.
 * Failed direct links surface via targets with status "failed" on the same
 * prompt. variant is null when the advertiser is not classified.
 * agencies/{agencyID}/isAppSumo forces variant "agency".
 * Optional RTDB: agencyLinkBatches/{batchID} (userID === uid) for batch
 * toasts only; not used for per-account status in the sheet or picker.
 */
export const postAgencyLinkingEndpoint = async ({
  endpoint,
  token,
  data = {},
}) => {
  try {
    const response = await axios({
      method: "post",
      url: `${getCloudFunctionUrl(endpoint)}/${endpoint}`,
      data,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data ?? { ok: false };
  } catch (error) {
    const body = error?.response?.data;
    if (body && typeof body === "object") return body;
    throw error;
  }
};
